import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2Icon } from 'lucide-react';
import {
  Button,
  DatePicker,
  EmptyState,
  ErrorNotice,
  Field,
  LoadingState,
  Panel,
  TextInput,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  createAnalyticsEvent,
  deleteAnalyticsEvent,
  listAnalyticsEvents,
  type AnalyticsEvent,
} from '@/lib/analytics-events';

/** React Query key for the whole key-events collection (panel list + chart markers). */
export const ANALYTICS_EVENTS_KEY = ['analytics-events'] as const;

/** "Aug 28, 2026" for an ISO instant, in UTC (the day was anchored to UTC midnight). */
function eventDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * The global key-events timeline: the list of logged events, an add form
 * (date + label + optional note) and per-row delete. Every change invalidates
 * the shared events query so the chart markers update live. Shown on the
 * page-types overview; the events are global, so they mark every analytics
 * chart.
 *
 * @returns The key-events panel.
 */
export function KeyEventsPanel(): React.ReactElement {
  const queryClient = useQueryClient();
  const events = useQuery({
    queryKey: [...ANALYTICS_EVENTS_KEY, 'all'],
    queryFn: ({ signal }) => listAnalyticsEvents(undefined, signal),
  });

  const [day, setDay] = useState('');
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ANALYTICS_EVENTS_KEY });
  const add = useMutation({
    mutationFn: () =>
      createAnalyticsEvent({ label: label.trim(), occursAt: day, ...(note.trim() ? { note: note.trim() } : {}) }),
    onSuccess: () => {
      setDay('');
      setLabel('');
      setNote('');
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteAnalyticsEvent(id),
    onSuccess: invalidate,
  });

  const canAdd = day !== '' && label.trim() !== '' && !add.isPending;
  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (canAdd) add.mutate();
  };

  return (
    <Panel className="mt-4 p-5">
      <div className="mb-3">
        <h2 className="text-portal-card-title font-bold text-portal-ink">Key events</h2>
        <p className="text-portal-micro text-portal-muted">
          Log what changed — a launch, a migration, a campaign — and it appears as a marker on every analytics chart.
        </p>
      </div>

      <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
        <Field label="Date" htmlFor="key-event-date" required>
          <DatePicker
            id="key-event-date"
            value={day === '' ? undefined : day}
            onChange={setDay}
            aria-label="Event date"
          />
        </Field>
        <Field label="Label" htmlFor="key-event-label" required hint="Short — it labels the marker.">
          <TextInput
            id="key-event-label"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Site transition"
            maxLength={120}
          />
        </Field>
        <Field label="Note" htmlFor="key-event-note" hint="Optional. Shown on hover.">
          <TextInput
            id="key-event-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="DNS cut to Railway"
            maxLength={500}
          />
        </Field>
        <Button type="submit" disabled={!canAdd}>
          {add.isPending ? 'Adding…' : 'Add event'}
        </Button>
      </form>
      {add.isError ? (
        <div className="mt-3">
          <ErrorNotice title="Could not add event" message={errorMessage(add.error)} />
        </div>
      ) : null}

      <div className="mt-4">
        {events.isPending ? (
          <LoadingState label="Loading key events…" />
        ) : events.isError ? (
          <ErrorNotice title="Key events unavailable" message={errorMessage(events.error)} />
        ) : events.data.length === 0 ? (
          <EmptyState
            title="No key events yet"
            message="Add the first event above — a site launch or migration — to mark it on every chart."
          />
        ) : (
          <ul className="divide-y divide-portal-line">
            {events.data.map((event: AnalyticsEvent) => (
              <li key={event.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="text-portal-small font-bold text-portal-ink">
                    {eventDay(event.occursAt)} · {event.label}
                  </p>
                  {event.note ? <p className="text-portal-micro text-portal-muted">{event.note}</p> : null}
                </div>
                <button
                  type="button"
                  onClick={() => remove.mutate(event.id)}
                  disabled={remove.isPending}
                  className="shrink-0 text-portal-muted transition-colors hover:text-rose-700"
                  aria-label={`Delete key event ${event.label}`}
                  title="Delete"
                >
                  <Trash2Icon className="size-4" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}
